import { useState, type ChangeEvent } from 'react';
import { planErrorMessage } from '../../components/plan/planErrors';
import { usePlanName } from '../../components/plan/usePlan';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { IMAGE_ACCEPT_ATTRIBUTE, uploadImageToAssets, validateImageFile } from '../../lib/storage';
import { AssetPreview } from '../knowledge-base/AssetPreview';
import { assetsApi } from '../knowledge-base/knowledgeBaseApi';
import type { CommunityZones } from '../../types/api';
import { importCommunityTemplateDocument } from './communityTemplatesApi';
import { PDF_ACCEPT_ATTRIBUTE, validatePdfFile } from './communityImport';
import { parseCommunityZones } from './communityZones';

/** Message for the domain errors the import can answer with. */
function importMessage(caught: unknown): string {
  if (caught instanceof ApiError && caught.code === 'TEMPLATE_IMPORT_INVALID') {
    return 'That document could not be read. Export it as a PDF from the app you designed it in and try again.';
  }
  return getErrorMessage(caught, 'That document did not import. Try again.');
}

export interface CommunityDesignSourceProps {
  businessId: string;
  /** The design already on the template, or null on a new one. */
  imagePath: string | null;
  /**
   * A design is on file: its storage path, plus the zone map the import read
   * off the document when it could read one.
   */
  onPicked: (imagePath: string, suggestedZones: CommunityZones | null) => void;
  disabled?: boolean;
}

/**
 * Where the design comes from: an image the member exports themselves, or a
 * PDF we rasterise for them.
 *
 * Two paths side by side rather than a mode switch, because they are two tools
 * the member already has rather than two settings of one. The upload path is
 * the app's own direct-to-Storage upload — the API never proxies those bytes,
 * and the file is an ordinary asset row afterwards. The import path is the one
 * call in the feature that does ship bytes, because the rasterising is the
 * server's.
 */
export function CommunityDesignSource({
  businessId,
  imagePath,
  onPicked,
  disabled = false,
}: CommunityDesignSourceProps) {
  const planName = usePlanName();
  const [isWorking, setIsWorking] = useState<'upload' | 'import' | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleUpload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';
    if (!file) return;

    const rejected = validateImageFile(file);
    if (rejected) {
      setError(rejected);
      return;
    }

    setError(null);
    setIsWorking('upload');
    try {
      const storagePath = await uploadImageToAssets(businessId, file);
      // The design is a normal asset row afterwards, like every other upload.
      await assetsApi.create(businessId, {
        storage_path: storagePath,
        kind: 'other',
        tags: [],
        product_id: null,
      });
      onPicked(storagePath, null);
    } catch (caught) {
      setError(planErrorMessage(caught, 'Upload failed. Try again.', { plan: planName }));
    } finally {
      setIsWorking(null);
    }
  }

  async function handleImport(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';
    if (!file) return;

    const rejected = validatePdfFile(file);
    if (rejected) {
      setError(rejected);
      return;
    }

    setError(null);
    setIsWorking('import');
    try {
      const imported = await importCommunityTemplateDocument(businessId, file);
      // The suggestion is read off a document, so it goes past the same guard
      // any other outside map does before it reaches the editor.
      onPicked(imported.image_path, parseCommunityZones(imported.suggested_zones));
    } catch (caught) {
      setError(importMessage(caught));
    } finally {
      setIsWorking(null);
    }
  }

  const isBusy = disabled || isWorking !== null;

  return (
    <fieldset className="community-source" disabled={disabled}>
      <legend className="field__label">The design</legend>
      <p className="field__hint">
        A finished square design, the way you would post it. Your text boxes, shapes, logo and photo
        are placed on top of it — nothing about the artwork is redrawn.
      </p>

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}

      {imagePath ? (
        <div className="community-source__current">
          <AssetPreview storagePath={imagePath} label="Your design" />
          <p className="field__hint">
            This design is uploaded. Upload another to replace it and your layers stay where they
            are; an import suggests new text boxes.
          </p>
        </div>
      ) : null}

      <div className="community-source__options">
        <div className="community-source__option">
          <span className="community-source__title">Upload an image</span>
          <p className="field__hint">PNG, JPEG, WebP or GIF, under 5 MB.</p>
          <label className="button" aria-disabled={isBusy}>
            {isWorking === 'upload' ? 'Uploading' : imagePath ? 'Replace image' : 'Choose image'}
            <input
              className="visually-hidden"
              type="file"
              accept={IMAGE_ACCEPT_ATTRIBUTE}
              disabled={isBusy}
              onChange={(event) => void handleUpload(event)}
            />
          </label>
        </div>

        <div className="community-source__option">
          <span className="community-source__title">Import from PDF</span>
          <p className="field__hint">
            Works with Canva, Figma, PowerPoint — export as PDF. We use page one, and suggest where
            the text boxes go.
          </p>
          <label className="button" aria-disabled={isBusy}>
            {isWorking === 'import' ? 'Importing' : imagePath ? 'Replace from PDF' : 'Choose PDF'}
            <input
              className="visually-hidden"
              type="file"
              accept={PDF_ACCEPT_ATTRIBUTE}
              disabled={isBusy}
              onChange={(event) => void handleImport(event)}
            />
          </label>
        </div>
      </div>
    </fieldset>
  );
}
